import { useCallback, useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import {
  ShieldCheck,
  Loader2,
  Save,
  KeyRound,
  LogOut,
  Smartphone,
  Trash2,
  CheckCircle2,
  AlertTriangle,
  KeySquare,
  Copy,
  Download,
  RefreshCw,
  EyeOff,
  History,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  generateRecoveryCodes,
  countRecoveryCodes,
  listRecoveryAudit,
  logExportVerificationFailure,
} from "@/lib/recovery-codes.functions";
import { PageHeader } from "./profiel";

export const Route = createFileRoute("/_authenticated/account/beveiliging")({
  head: () => ({
    meta: [
      { title: "Beveiliging — Elvion Group Nederland" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: SecurityPage,
});

function SecurityPage() {
  const { user } = useAuth();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  const change = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pw.length < 8) {
      setMsg({ kind: "err", text: "Wachtwoord moet minimaal 8 tekens zijn." });
      return;
    }
    if (pw !== pw2) {
      setMsg({ kind: "err", text: "Wachtwoorden komen niet overeen." });
      return;
    }
    setSaving(true);
    const { error } = await supabase.auth.updateUser({ password: pw });
    setSaving(false);
    if (error) {
      setMsg({ kind: "err", text: error.message });
      return;
    }
    setMsg({ kind: "ok", text: "Wachtwoord bijgewerkt." });
    setPw("");
    setPw2("");
    setTimeout(() => setMsg(null), 3000);
  };

  const signOutAll = async () => {
    if (!confirm("Op alle apparaten uitloggen?")) return;
    await supabase.auth.signOut({ scope: "global" });
    window.location.href = "/auth";
  };

  return (
    <div className="space-y-8">
      <PageHeader
        icon={ShieldCheck}
        title="Beveiliging"
        subtitle="Beheer uw wachtwoord, tweestapsverificatie en actieve sessies."
      />

      <form
        onSubmit={change}
        className="rounded-2xl border border-border bg-card p-6 shadow-sm sm:p-8"
      >
        <div className="mb-5 flex items-center gap-3">
          <div className="flex size-10 items-center justify-center rounded-xl bg-gradient-to-br from-[#0b2a4a] to-[#3a7bd5] text-white">
            <KeyRound className="size-4" />
          </div>
          <div>
            <h2 className="font-display text-lg font-bold">Wachtwoord wijzigen</h2>
            <p className="text-xs text-text-secondary">
              Gebruik een sterk, uniek wachtwoord van minimaal 8 tekens.
            </p>
          </div>
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Nieuw wachtwoord">
            <input
              type="password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              className={input}
              required
              minLength={8}
            />
          </Field>
          <Field label="Bevestig wachtwoord">
            <input
              type="password"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              className={input}
              required
              minLength={8}
            />
          </Field>
        </div>
        <div className="mt-6 flex items-center justify-between gap-3 border-t border-border pt-5">
          {msg ? (
            <p
              className={`text-sm font-medium ${
                msg.kind === "ok" ? "text-emerald-600" : "text-red-600"
              }`}
            >
              {msg.text}
            </p>
          ) : (
            <span className="text-xs text-text-secondary">Ingelogd als {user?.email}</span>
          )}
          <Button type="submit" disabled={saving} className="gap-2">
            {saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
            Bijwerken
          </Button>
        </div>
      </form>

      <TwoFactorCard />

      <RecoveryCodesCard />

      <div className="rounded-2xl border border-border bg-card p-6 shadow-sm sm:p-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-display text-lg font-bold">Actieve sessies</h2>
            <p className="mt-1 text-sm text-text-secondary">
              Log uit op alle apparaten waar uw account actief is.
            </p>
          </div>
          <Button variant="outline" onClick={signOutAll} className="gap-2">
            <LogOut className="size-4" />
            Overal uitloggen
          </Button>
        </div>
      </div>
    </div>
  );
}

type Factor = { id: string; friendly_name?: string | null; status: string; factor_type: string };

function TwoFactorCard() {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [factors, setFactors] = useState<Factor[]>([]);
  const [enrolling, setEnrolling] = useState<
    | null
    | {
        factorId: string;
        qr: string;
        secret: string;
        uri: string;
      }
  >(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const refresh = async () => {
    setLoading(true);
    const { data } = await supabase.auth.mfa.listFactors();
    setFactors((data?.totp ?? []) as Factor[]);
    setLoading(false);
  };

  useEffect(() => {
    refresh();
  }, []);

  const verified = factors.filter((f) => f.status === "verified");
  const hasVerified = verified.length > 0;

  const startEnroll = async () => {
    setErr(null);
    setOk(null);
    setBusy(true);
    // Clean up any previous unverified enrollments
    const unverified = factors.filter((f) => f.status !== "verified");
    for (const f of unverified) {
      await supabase.auth.mfa.unenroll({ factorId: f.id });
    }
    const { data, error } = await supabase.auth.mfa.enroll({
      factorType: "totp",
      friendlyName: `Authenticator ${new Date().toLocaleDateString("nl-NL")}`,
    });
    setBusy(false);
    if (error || !data) {
      setErr(error?.message ?? "Kon enrollment niet starten.");
      return;
    }
    setEnrolling({
      factorId: data.id,
      qr: data.totp.qr_code,
      secret: data.totp.secret,
      uri: data.totp.uri,
    });
  };

  const cancelEnroll = async () => {
    if (!enrolling) return;
    await supabase.auth.mfa.unenroll({ factorId: enrolling.factorId });
    setEnrolling(null);
    setCode("");
    refresh();
  };

  const verifyEnroll = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!enrolling) return;
    setErr(null);
    setBusy(true);
    const { data: ch, error: chErr } = await supabase.auth.mfa.challenge({
      factorId: enrolling.factorId,
    });
    if (chErr || !ch) {
      setBusy(false);
      setErr(chErr?.message ?? "Challenge mislukt.");
      return;
    }
    const { error } = await supabase.auth.mfa.verify({
      factorId: enrolling.factorId,
      challengeId: ch.id,
      code: code.trim(),
    });
    setBusy(false);
    if (error) {
      setErr(error.message);
      return;
    }
    setOk("Tweestapsverificatie ingeschakeld.");
    setEnrolling(null);
    setCode("");
    refresh();
  };

  const remove = async (factorId: string) => {
    if (!confirm("Tweestapsverificatie verwijderen voor dit account?")) return;
    setBusy(true);
    const { error } = await supabase.auth.mfa.unenroll({ factorId });
    setBusy(false);
    if (error) {
      setErr(error.message);
      return;
    }
    setOk("Tweestapsverificatie uitgeschakeld.");
    refresh();
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-6 shadow-sm sm:p-8">
      <div className="mb-5 flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex size-10 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-600 to-emerald-400 text-white">
            <Smartphone className="size-4" />
          </div>
          <div>
            <h2 className="font-display text-lg font-bold">Tweestapsverificatie (2FA)</h2>
            <p className="text-xs text-text-secondary">
              Beveilig uw account met een authenticator-app (Google Authenticator, 1Password, Authy).
            </p>
          </div>
        </div>
        {hasVerified ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
            <CheckCircle2 className="size-3.5" /> Actief
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700">
            <AlertTriangle className="size-3.5" /> Niet actief
          </span>
        )}
      </div>

      {isAdmin && !hasVerified && (
        <div className="mb-5 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <p>
            <strong>Sterk aanbevolen voor beheerders.</strong> Schakel 2FA in om uw admin-account te
            beschermen tegen ongeautoriseerde toegang.
          </p>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-text-secondary">
          <Loader2 className="size-4 animate-spin" /> Laden…
        </div>
      ) : enrolling ? (
        <form onSubmit={verifyEnroll} className="space-y-4">
          <div className="grid gap-5 sm:grid-cols-[auto_1fr] sm:items-start">
            <div
              className="rounded-lg border border-border bg-white p-3"
              dangerouslySetInnerHTML={{ __html: enrolling.qr }}
            />
            <div className="space-y-3 text-sm">
              <p>
                <strong>1.</strong> Scan de QR-code met uw authenticator-app, óf voer de geheime
                sleutel handmatig in.
              </p>
              <div>
                <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-text-secondary">
                  Geheime sleutel
                </span>
                <code className="block break-all rounded-md bg-muted px-3 py-2 text-xs">
                  {enrolling.secret}
                </code>
              </div>
              <p>
                <strong>2.</strong> Voer de 6-cijferige code uit de app hieronder in om te
                bevestigen.
              </p>
              <Field label="Verificatiecode">
                <input
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  className={input}
                  placeholder="123456"
                  required
                />
              </Field>
            </div>
          </div>
          {err && <p className="text-sm font-medium text-red-600">{err}</p>}
          <div className="flex items-center gap-2 border-t border-border pt-4">
            <Button type="submit" disabled={busy || code.length !== 6} className="gap-2">
              {busy ? <Loader2 className="size-4 animate-spin" /> : <CheckCircle2 className="size-4" />}
              Bevestigen en inschakelen
            </Button>
            <Button type="button" variant="outline" onClick={cancelEnroll} disabled={busy}>
              Annuleren
            </Button>
          </div>
        </form>
      ) : hasVerified ? (
        <div className="space-y-3">
          {verified.map((f) => (
            <div
              key={f.id}
              className="flex items-center justify-between gap-3 rounded-lg border border-border bg-background p-3"
            >
              <div>
                <p className="text-sm font-semibold">{f.friendly_name || "Authenticator"}</p>
                <p className="text-xs text-text-secondary">TOTP · geverifieerd</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => remove(f.id)}
                disabled={busy}
                className="gap-1.5 text-red-600 hover:text-red-700"
              >
                <Trash2 className="size-3.5" /> Verwijderen
              </Button>
            </div>
          ))}
          {ok && <p className="text-sm font-medium text-emerald-600">{ok}</p>}
          {err && <p className="text-sm font-medium text-red-600">{err}</p>}
        </div>
      ) : (
        <div>
          <p className="mb-4 text-sm text-text-secondary">
            Bij elke login vraagt het systeem na uw wachtwoord om een 6-cijferige code uit uw
            authenticator-app.
          </p>
          {err && <p className="mb-3 text-sm font-medium text-red-600">{err}</p>}
          {ok && <p className="mb-3 text-sm font-medium text-emerald-600">{ok}</p>}
          <Button onClick={startEnroll} disabled={busy} className="gap-2">
            {busy ? <Loader2 className="size-4 animate-spin" /> : <ShieldCheck className="size-4" />}
            2FA inschakelen
          </Button>
        </div>
      )}
    </div>
  );
}

const input =
  "w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm focus:border-[#3a7bd5] focus:outline-none focus:ring-2 focus:ring-[#3a7bd5]/20";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-text-secondary">
        {label}
      </span>
      {children}
    </label>
  );
}

function RecoveryCodesCard() {
  const { user, isAdmin } = useAuth();
  const generateFn = useServerFn(generateRecoveryCodes);
  const countFn = useServerFn(countRecoveryCodes);
  const auditFn = useServerFn(listRecoveryAudit);
  const logExportFailFn = useServerFn(logExportVerificationFailure);

  const [counts, setCounts] = useState<{ total: number; unused: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [codes, setCodes] = useState<string[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [savedConfirmOpen, setSavedConfirmOpen] = useState(false);
  const [exportConfirmOpen, setExportConfirmOpen] = useState(false);
  const [exportStep, setExportStep] = useState<"warn" | "verify">("warn");
  const [exportMethod, setExportMethod] = useState<"totp" | "password">("password");
  const [exportCode, setExportCode] = useState("");
  const [exportPw, setExportPw] = useState("");
  const [exportBusy, setExportBusy] = useState(false);
  const [exportErr, setExportErr] = useState<string | null>(null);
  const [exportAttempts, setExportAttempts] = useState(0);
  const [exportCooldownUntil, setExportCooldownUntil] = useState<number | null>(null);
  const [exportNow, setExportNow] = useState(Date.now());

  const EXPORT_MAX_ATTEMPTS = 5;
  const EXPORT_COOLDOWN_MS = 60_000;

  useEffect(() => {
    if (!exportCooldownUntil) return;
    const t = setInterval(() => {
      const now = Date.now();
      setExportNow(now);
      if (now >= exportCooldownUntil) {
        setExportCooldownUntil(null);
        setExportAttempts(0);
        setExportErr(null);
      }
    }, 1000);
    return () => clearInterval(t);
  }, [exportCooldownUntil]);

  const exportCooldownRemaining = exportCooldownUntil
    ? Math.max(0, Math.ceil((exportCooldownUntil - exportNow) / 1000))
    : 0;
  const exportLocked = exportCooldownRemaining > 0;

  const [auditEntries, setAuditEntries] = useState<
    Array<{
      id: string;
      event:
        | "generated"
        | "consumed"
        | "export_verification_failed"
        | "export_verification_blocked";
      code_count: number | null;
      ip_address: string | null;
      user_agent: string | null;
      created_at: string;
    }>
  >([]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [c, a] = await Promise.all([countFn(), auditFn()]);
      setCounts(c);
      setAuditEntries(a.entries as typeof auditEntries);
    } catch {
      setCounts({ total: 0, unused: 0 });
    }
    setLoading(false);
  }, [countFn, auditFn]);

  useEffect(() => {
    refresh();
  }, [refresh]);




  const doGenerate = useCallback(async () => {
    setBusy(true);
    setErr(null);
    try {
      const { codes: list } = await generateFn();
      setCodes(list);
      await refresh();
    } catch (e: any) {
      setErr(e?.message ?? "Genereren mislukt.");
    }
    setBusy(false);
    setConfirming(false);
  }, [generateFn, refresh]);

  const confirmSaved = useCallback(() => {
    setCodes(null);
    setSavedConfirmOpen(false);
  }, []);

  const copyAll = useCallback(async () => {
    if (!codes) return;
    await navigator.clipboard.writeText(codes.join("\n"));
  }, [codes]);

  const download = useCallback(() => {
    if (!codes) return;
    const content = [
      "Elvion Group Nederland — 2FA herstelcodes",
      `Account: ${user?.email ?? ""}`,
      `Gegenereerd: ${new Date().toLocaleString("nl-NL")}`,
      "",
      "Bewaar deze codes op een veilige plek (wachtwoordmanager, kluis).",
      "Elke code werkt één keer en geeft toegang als u uw authenticator-app kwijt bent.",
      "",
      ...codes,
    ].join("\n");
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `elvion-herstelcodes-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }, [codes, user?.email]);

  const eventLabel = useCallback(
    (event: (typeof auditEntries)[number]["event"]) =>
      event === "generated"
        ? "Gegenereerd"
        : event === "consumed"
          ? "Gebruikt"
          : event === "export_verification_failed"
            ? "Export-verificatie mislukt"
            : "Export geblokkeerd",
    [],
  );

  const exportAuditCsv = useCallback(() => {
    if (!auditEntries.length) return;
    const headers = ["ID", "Gebeurtenis", "Aantal", "IP-adres", "User-Agent", "Datum/tijd"];
    const rows = auditEntries.map((e) => [
      e.id,
      eventLabel(e.event),
      e.code_count?.toString() ?? "",
      e.ip_address ?? "",
      (e.user_agent ?? "").replace(/"/g, '""'),
      new Date(e.created_at).toLocaleString("nl-NL"),
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map((c) => `"${c}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `elvion-auditlog-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [auditEntries, eventLabel]);

  const hasCodes = (counts?.total ?? 0) > 0;
  const lowOrNone = !counts || counts.unused === 0 || counts.unused < 3;
  const recentExportFailures = useMemo(
    () =>
      auditEntries.reduce(
        (n, e) =>
          e.event === "export_verification_failed" ||
          e.event === "export_verification_blocked"
            ? n + 1
            : n,
        0,
      ),
    [auditEntries],
  );

  return (
    <div className="rounded-2xl border border-border bg-card p-6 shadow-sm sm:p-8">
      <div className="mb-5 flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex size-10 items-center justify-center rounded-xl bg-gradient-to-br from-amber-600 to-amber-400 text-white">
            <KeySquare className="size-4" />
          </div>
          <div>
            <h2 className="font-display text-lg font-bold">Herstelcodes</h2>
            <p className="text-xs text-text-secondary">
              Eenmalige codes om in te loggen als u uw authenticator-app verliest.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {recentExportFailures > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-1 text-xs font-bold text-red-700 animate-pulse">
              <AlertTriangle className="size-3.5" />
              {recentExportFailures} mislukte exportpoging{recentExportFailures === 1 ? "" : "en"}
            </span>
          )}
          {hasCodes && counts && (
            <span
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${
                counts.unused > 3
                  ? "bg-emerald-50 text-emerald-700"
                  : counts.unused > 0
                    ? "bg-amber-50 text-amber-700"
                    : "bg-red-50 text-red-700"
              }`}
            >
              {counts.unused} van {counts.total} ongebruikt
            </span>
          )}
        </div>
      </div>

      {isAdmin && lowOrNone && (
        <div className="mb-5 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <p>
            <strong>Belangrijk voor beheerders.</strong>{" "}
            {hasCodes
              ? "U heeft weinig of geen ongebruikte herstelcodes meer. Genereer nieuwe codes en bewaar ze veilig."
              : "Genereer herstelcodes zodat u nooit buitengesloten raakt."}
          </p>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-text-secondary">
          <Loader2 className="size-4 animate-spin" /> Laden…
        </div>
      ) : codes ? (
        <div className="space-y-4">
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
            <strong>Bewaar deze codes nu.</strong> Ze worden maar één keer getoond. Sla ze op in een
            wachtwoordmanager of print ze.
          </div>
          <div className="grid grid-cols-2 gap-2 rounded-lg border border-border bg-background p-4 font-mono text-sm sm:grid-cols-2">
            {codes.map((c) => (
              <div key={c} className="rounded bg-muted px-2 py-1.5 text-center tracking-wider">
                {c}
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={copyAll} variant="outline" size="sm" className="gap-1.5">
              <Copy className="size-3.5" /> Kopieer alle
            </Button>
            <Button onClick={download} variant="outline" size="sm" className="gap-1.5">
              <Download className="size-3.5" /> Download .txt
            </Button>
            <Button
              onClick={() => setSavedConfirmOpen(true)}
              size="sm"
              className="ml-auto gap-1.5"
            >
              <CheckCircle2 className="size-3.5" /> Ik heb ze bewaard
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-text-secondary">
            Elke code werkt eenmalig. Bij gebruik wordt 2FA tijdelijk uitgeschakeld zodat u opnieuw
            kunt inloggen — koppel daarna direct een nieuwe authenticator-app.
          </p>
          {err && <p className="text-sm font-medium text-red-600">{err}</p>}
          {confirming ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <p className="mb-3">
                Bestaande herstelcodes worden ongeldig. Doorgaan?
              </p>
              <div className="flex gap-2">
                <Button onClick={doGenerate} disabled={busy} size="sm" className="gap-1.5">
                  {busy ? (
                    <Loader2 className="size-3.5 animate-spin" />
                  ) : (
                    <RefreshCw className="size-3.5" />
                  )}
                  Ja, nieuwe codes
                </Button>
                <Button
                  onClick={() => setConfirming(false)}
                  variant="outline"
                  size="sm"
                  disabled={busy}
                >
                  Annuleren
                </Button>
              </div>
            </div>
          ) : (
            <Button
              onClick={hasCodes ? () => setConfirming(true) : doGenerate}
              disabled={busy}
              className="gap-2"
            >
              {busy ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <KeySquare className="size-4" />
              )}
              {hasCodes ? "Nieuwe herstelcodes genereren" : "Herstelcodes genereren"}
            </Button>
          )}
        </div>
      )}

      {auditEntries.length > 0 && (
        <div className="mt-6 border-t border-border pt-5">
          <div className="mb-3 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <History className="size-4 text-text-secondary" />
              <h3 className="text-sm font-semibold">Activiteit herstelcodes</h3>
            </div>
            <Button
              onClick={async () => {
                setExportErr(null);
                setExportCode("");
                setExportPw("");
                setExportStep("warn");
                const { data } = await supabase.auth.mfa.listFactors();
                const hasTotp = (data?.totp ?? []).some((f) => f.status === "verified");
                setExportMethod(hasTotp ? "totp" : "password");
                setExportConfirmOpen(true);
              }}
              variant="outline"
              size="sm"
              className="gap-1.5"
            >
              <Download className="size-3.5" /> Exporteer CSV
            </Button>
          </div>
          <ul className="space-y-2">
            {auditEntries.map((e) => (
              <li
                key={e.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-background px-3 py-2 text-xs"
              >
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold ${
                      e.event === "generated"
                        ? "bg-blue-50 text-blue-700"
                        : e.event === "consumed"
                          ? "bg-amber-50 text-amber-700"
                          : e.event === "export_verification_failed"
                            ? "bg-orange-50 text-orange-700"
                            : "bg-red-50 text-red-700"
                    }`}
                  >
                    {e.event === "generated" ? (
                      <>
                        <RefreshCw className="size-3" /> Gegenereerd
                        {e.code_count ? ` (${e.code_count})` : ""}
                      </>
                    ) : e.event === "consumed" ? (
                      <>
                        <KeySquare className="size-3" /> Gebruikt
                      </>
                    ) : e.event === "export_verification_failed" ? (
                      <>
                        <AlertTriangle className="size-3" /> Export-verificatie mislukt
                      </>
                    ) : (
                      <>
                        <AlertTriangle className="size-3" /> Export geblokkeerd
                      </>
                    )}
                  </span>
                  {e.ip_address && (
                    <span className="text-text-secondary">IP {e.ip_address}</span>
                  )}
                </div>
                <span className="text-text-secondary">
                  {new Date(e.created_at).toLocaleString("nl-NL")}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Dialog open={savedConfirmOpen} onOpenChange={setSavedConfirmOpen}>

        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <EyeOff className="size-5" />
              Herstelcodes verbergen?
            </DialogTitle>
            <DialogDescription>
              De codes worden nu verborgen en zijn daarna niet meer te zien. Controleer of u ze
              veilig heeft opgeslagen.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSavedConfirmOpen(false)}>
              Annuleren
            </Button>
            <Button onClick={confirmSaved} className="gap-1.5">
              <CheckCircle2 className="size-4" />
              Ja, ik heb ze bewaard
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={exportConfirmOpen}
        onOpenChange={(o) => {
          setExportConfirmOpen(o);
          if (!o) {
            setExportStep("warn");
            setExportCode("");
            setExportPw("");
            setExportErr(null);
          }
        }}
      >
        <DialogContent>
          {exportStep === "warn" ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-amber-700">
                  <AlertTriangle className="size-5" />
                  Gevoelige informatie exporteren
                </DialogTitle>
                <DialogDescription>
                  Dit CSV-bestand bevat gevoelige informatie zoals IP-adressen en
                  gebruikersgegevens. U moet uw identiteit opnieuw bevestigen voordat de export
                  start. Bewaar het bestand alleen op een veilige plek.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setExportConfirmOpen(false)}>
                  Annuleren
                </Button>
                <Button onClick={() => setExportStep("verify")} className="gap-1.5">
                  Doorgaan
                </Button>
              </DialogFooter>
            </>
          ) : (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (exportLocked) return;
                setExportBusy(true);
                setExportErr(null);
                try {
                  if (exportMethod === "totp") {
                    const { data: factors } = await supabase.auth.mfa.listFactors();
                    const factor = (factors?.totp ?? []).find((f) => f.status === "verified");
                    if (!factor) throw new Error("Geen 2FA-factor gevonden.");
                    const { error } = await supabase.auth.mfa.challengeAndVerify({
                      factorId: factor.id,
                      code: exportCode.trim(),
                    });
                    if (error) throw error;
                  } else {
                    if (!user?.email) throw new Error("Geen e-mailadres bekend.");
                    const { error } = await supabase.auth.signInWithPassword({
                      email: user.email,
                      password: exportPw,
                    });
                    if (error) throw error;
                  }
                  setExportConfirmOpen(false);
                  setExportStep("warn");
                  setExportCode("");
                  setExportPw("");
                  setExportAttempts(0);
                  setExportCooldownUntil(null);
                  exportAuditCsv();
                } catch (err: any) {
                  const next = exportAttempts + 1;
                  setExportAttempts(next);
                  setExportCode("");
                  setExportPw("");
                  const blocked = next >= EXPORT_MAX_ATTEMPTS;
                  if (blocked) {
                    const until = Date.now() + EXPORT_COOLDOWN_MS;
                    setExportCooldownUntil(until);
                    setExportNow(Date.now());
                    setExportErr(
                      `Te veel mislukte pogingen. Probeer het over ${Math.ceil(
                        EXPORT_COOLDOWN_MS / 1000,
                      )} seconden opnieuw. Deze gebeurtenis is vastgelegd in het auditlog.`,
                    );
                  } else {
                    const remaining = EXPORT_MAX_ATTEMPTS - next;
                    const base = err?.message ?? "Verificatie mislukt.";
                    setExportErr(
                      `${base} Nog ${remaining} ${remaining === 1 ? "poging" : "pogingen"} over voordat de export tijdelijk wordt geblokkeerd.`,
                    );
                  }
                  // Audit log — fire-and-forget; never blocks the UI
                  void logExportFailFn({ data: { blocked } })
                    .then(() => refresh())
                    .catch(() => {});
                } finally {
                  setExportBusy(false);
                }
              }}
            >
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <ShieldCheck className="size-5" />
                  Bevestig uw identiteit
                </DialogTitle>
                <DialogDescription>
                  {exportMethod === "totp"
                    ? "Voer een 6-cijferige code uit uw authenticator-app in om de export te starten."
                    : "Voer uw wachtwoord opnieuw in om de export te starten."}
                </DialogDescription>
              </DialogHeader>
              <div className="py-2">
                {exportMethod === "totp" ? (
                  <Field label="Verificatiecode">
                    <input
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      pattern="[0-9]{6}"
                      maxLength={6}
                      value={exportCode}
                      onChange={(e) => setExportCode(e.target.value.replace(/\D/g, ""))}
                      className={input}
                      placeholder="123456"
                      autoFocus
                      required
                      disabled={exportLocked}
                    />
                  </Field>
                ) : (
                  <Field label="Wachtwoord">
                    <input
                      type="password"
                      autoComplete="current-password"
                      value={exportPw}
                      onChange={(e) => setExportPw(e.target.value)}
                      className={input}
                      autoFocus
                      required
                      disabled={exportLocked}
                    />
                  </Field>
                )}
                {exportLocked ? (
                  <div className="mt-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                    <AlertTriangle className="mt-0.5 size-4 shrink-0" />
                    <p>
                      Te veel mislukte pogingen. Probeer het opnieuw over{" "}
                      <strong>{exportCooldownRemaining}s</strong>.
                    </p>
                  </div>
                ) : (
                  exportErr && (
                    <p className="mt-3 text-sm font-medium text-red-600">{exportErr}</p>
                  )
                )}
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setExportConfirmOpen(false)}
                  disabled={exportBusy}
                >
                  Annuleren
                </Button>
                <Button
                  type="submit"
                  className="gap-1.5"
                  disabled={
                    exportBusy ||
                    exportLocked ||
                    (exportMethod === "totp" ? exportCode.length !== 6 : exportPw.length < 1)
                  }
                >
                  {exportBusy ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <Download className="size-4" />
                  )}
                  {exportLocked
                    ? `Wacht ${exportCooldownRemaining}s`
                    : "Bevestigen en exporteren"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
